import React, { useState, useEffect } from 'react';
import { 
  Box,
  Typography,
  Button,
  Stack,
  Paper,
  Grid,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Alert,
  CircularProgress,
  Collapse,
  Link,
  Snackbar,
  TextField,
  AlertColor,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material';
import { styled, Theme } from '@mui/material/styles';
import PhoneIcon from '@mui/icons-material/Phone';
import SimCardIcon from '@mui/icons-material/SimCard';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTheme } from '@mui/material/styles';
import { useParams } from 'react-router-dom';
import { PARTY_MANAGEMENT_API_URL, RESOURCE_INVENTORY_URL, SERVICE_ORDERING_API_URL } from '../../utils/constants';

interface StepIconProps {
  active: boolean;
  theme?: Theme;
}

const StepIcon = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active'
})<StepIconProps>(({ theme, active }) => ({
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: active ? theme.palette.primary.main : theme.palette.grey[400],
  color: active ? theme.palette.primary.contrastText : theme.palette.grey[100],
}));

const NumberButton = styled(Button)(({ theme }) => ({
  width: '100%',
  padding: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  '&:hover': {
    backgroundColor: theme.palette.primary.light,
    borderColor: theme.palette.primary.main,
  },
}));

export const BuyProduct = () => {
  const theme = useTheme();
  const [currentStep, setCurrentStep] = useState(1);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedNumber, setSelectedNumber] = useState('');
  const [simType, setSimType] = useState('esim');
  const [showMore, setShowMore] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [physicalSims, setPhysicalSims] = useState([]);
  const [eSims, setESims] = useState([]);
  const [selectedSim, setSelectedSim] = useState(null);
  const [individualId, setIndividualId] = useState(null);
  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // Form fields for step 2
  const [formData, setFormData] = useState({
    fullName: '',
    phoneNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    // Additional fields from API
    title: '',
    gender: '',
    birthDate: null,
    nationality: '',
    kyc: '',
    maritalStatus: '',
    preferredGivenName: '',
    location: {
      address: '',
      city: '',
      country: '',
      postcode: ''
    }
  });
  const genderOptions = ['Male', 'Female', 'Other', 'Prefer not to say'];
  const maritalStatusOptions = ['Single', 'Married', 'Divorced', 'Widowed'];
  const titleOptions = ['Mr', 'Mrs', 'Ms', 'Dr', 'Prof'];

  const params = useParams();

  const handleCreateIndividual = async () => {
    try {
      setIsSubmitting(true);
      // Split full name into given name and family name
      const nameParts = formData.fullName.split(' ');
      const givenName = nameParts[0];
      const familyName = nameParts.slice(1).join(' ');

      const individualData = {
        familyName,
        givenName,
        title: formData.title,
        gender: formData.gender,
        nationality: formData.nationality,
        maritalStatus: formData.maritalStatus,
        preferredGivenName: formData.preferredGivenName,
        location: formData.location,
        partyCharacteristic: [
          {
            name: "kyc",
            value: formData.kyc
          }
        ],
        contactMedium: [
          {
            mediumType: 'emailAddress',
            characteristic: {
              name: 'emailAddress',
              value: formData.email
            },
            preferred: true
          },
          {
            mediumType: 'phoneNumber',
            characteristic: {
              name: 'phoneNumber',
              value: formData.phoneNumber
            },
            preferred: false
          }
        ]
      };

      const response = await fetch(`${PARTY_MANAGEMENT_API_URL}/individual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(individualData)
      });

      if (!response.ok) {
        throw new Error('Failed to create individual');
      }

      const result = await response.json();
      setIndividualId(result.id);
      setCurrentStep(3);
      setToast({
        open: true,
        message: 'Account created successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating individual:', error);
      setToast({
        open: true,
        message: 'Failed to create account. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitOrder = async () => {
    try {
      setIsSubmitting(true);
      const orderData = {
        description: `Order for mobile line`,
        requestedStartDate: new Date().toISOString(),
        requestedCompletionDate: new Date().toISOString(),
        serviceOrderItem: [
          {
            id: "1",
            action: "add",
            service: {
              name: "Mobile Line",
              supportingResource: [
                {
                  id: selectedNumber,
                  baseType: "Resource",
                  type: "LogicalResource",
                  referredType: "MSISDN"
                },
                {
                  id: selectedSim,
                  baseType: "Resource",
                  type: simType === 'physical' ? 'PhysicalResource' : 'LogicalResource',
                  referredType: simType === 'physical' ? 'PSIM' : 'ESIM'
                }
              ],
              relatedParty: [
                {
                  id: individualId,
                  baseType: "Individual",
                  type: "Individual",
                  referredType: "Individual"
                }
              ],
              relatedEntity: [
                {
                  id: params.id?.split('_')[0],
                  baseType: "ProductOffering",
                  type: "ProductOffering",
                  referredType: "ProductOffering"
                },
                {
                  id: params.id?.split('_')[1],
                  baseType: "ProductPrice",
                  type: "ProductPrice",
                  referredType: "ProductPrice"
                }
              ]
            }
          }
        ]
      };

      const response = await fetch(`${SERVICE_ORDERING_API_URL}/serviceOrder`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(orderData)
      });

      if (!response.ok) {
        throw new Error('Failed to create service order');
      }

      setToast({
        open: true,
        message: 'Order submitted successfully!',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error creating service order:', error);
      setToast({
        open: true,
        message: 'Failed to submit order. Please try again.',
        severity: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    // Fetch phone numbers from resource endpoint
    const fetchPhoneNumbers = async () => {
      try {
        const response = await fetch(`${RESOURCE_INVENTORY_URL}/resource?type=MSISDN`);
        const data = await response.json();
        setPhoneNumbers(data);
      } catch (error) {
        console.error('Error fetching phone numbers:', error);
      } finally {
        setLoading(false);
      }
    };

    // Fetch physical SIMs
    const fetchPhysicalSims = async () => {
      try {
        const response = await fetch(`${RESOURCE_INVENTORY_URL}/resource?type=PSIM`);
        const data = await response.json();
        setPhysicalSims(data);
        if (data.length > 0) {
          setSelectedSim(data[0].id);
        }
      } catch (error) {
        console.error('Error fetching physical SIMs:', error);
      }
    };

    // Fetch eSIMs
    const fetchESims = async () => {
      try {
        const response = await fetch(`${RESOURCE_INVENTORY_URL}/resource?type=ESIM`);
        const data = await response.json();
        setESims(data);
        if (data.length > 0) {
          setSelectedSim(data[0].id);
        }
      } catch (error) {
        console.error('Error fetching eSIMs:', error);
      }
    };

    fetchPhoneNumbers();
    fetchPhysicalSims();
    fetchESims();
  }, []);

  // Reset selected SIM when sim type changes
  useEffect(() => {
    if (simType === 'physical' && physicalSims.length > 0) {
      setSelectedSim((physicalSims[0] as any).id);
    } else if (simType === 'esim' && eSims.length > 0) {
      setSelectedSim((eSims[0] as any).id);
    }
  }, [simType, physicalSims, eSims]);

  const renderStep1 = () => (
    <>
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6}>
          <Button 
            variant="contained" 
            fullWidth 
            sx={{ py: 2 }}
            color="primary"
          >
            GET A NEW NUMBER
          </Button>
        </Grid>
        <Grid item xs={6}>
          <Button 
            variant="contained" 
            fullWidth 
            sx={{ py: 2 }}
            color="secondary"
          >
            KEEP MY EXISTING NUMBER
          </Button>
        </Grid>
      </Grid>

      {/* Number Selection */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
          Choose number
        </Typography>
        
        {loading ? (
          <CircularProgress />
        ) : (
          <Grid container spacing={2}>
            {phoneNumbers.slice(0, showMore ? undefined : 6).map((number: any) => (
              <Grid item xs={12} sm={6} md={4} key={number.id}>
                <NumberButton
                  variant="outlined"
                  onClick={() => setSelectedNumber(number.id)}
                  sx={{
                    backgroundColor: selectedNumber === number.id ? 'primary.light' : 'transparent',
                    color: selectedNumber === number.id ? 'primary.contrastText' : 'primary.main',
                    '&:hover': {
                      color: 'primary.contrastText'
                    }
                  }}
                >
                  {number.value}
                </NumberButton>
              </Grid>
            ))}
          </Grid>
        )}
        
        {phoneNumbers.length > 6 && (
          <Button
            onClick={() => setShowMore(!showMore)}
            endIcon={<KeyboardArrowDownIcon />}
            sx={{ mt: 2 }}
          >
            {showMore ? 'Show less' : 'Show more'}
          </Button>
        )}
      </Box>

      {/* SIM Selection */}
      {selectedNumber && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" color="text.primary" sx={{ mb: 2 }}>
            Choose SIM Type
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup value={simType} onChange={(e) => setSimType(e.target.value)}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <FormControlLabel
                      value="physical"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="subtitle1">Physical SIM card</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Receive in 1 to 7 working days
                          </Typography>
                          <Typography variant="subtitle1" sx={{ mt: 1 }}>$2</Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: 2, height: '100%' }}>
                    <FormControlLabel
                      value="esim"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="subtitle1">eSIM</Typography>
                          <Typography variant="body2" color="text.secondary">
                            Receive and install instantly
                          </Typography>
                          <Typography variant="subtitle1" sx={{ mt: 1 }}>FREE</Typography>
                        </Box>
                      }
                    />
                  </Paper>
                </Grid>
              </Grid>
            </RadioGroup>
          </FormControl>
          
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 3 }}
            disabled={!selectedNumber || !selectedSim || isSubmitting}
            onClick={() => setCurrentStep(2)}
          >
            Proceed to Create Account
          </Button>
        </Box>
      )}
    </>
  );

  const renderStep2 = () => (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: 3 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>Create new account</Typography>
      
      <Box>
        <Stack spacing={3}>
          {/* Personal Information Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Personal Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Title</InputLabel>
                  <Select
                    value={formData.title}
                    label="Title"
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                  >
                    {titleOptions.map(title => (
                      <MenuItem key={title} value={title}>{title}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="Full Name"
                  value={formData.fullName}
                  onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Nationality"
                  value={formData.nationality}
                  onChange={(e) => setFormData(prev => ({ ...prev, nationality: e.target.value }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="KYC Document Number"
                  value={formData.kyc}
                  onChange={(e) => setFormData(prev => ({ ...prev, kyc: e.target.value }))}
                  required
                  helperText="Enter your ID/Passport number"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Contact Information Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Contact Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  value={formData.phoneNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, phoneNumber: e.target.value }))}
                  required
                  helperText="One-Time Password (OTP) will be sent to your mobile number"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  required
                  helperText="One-Time Password (OTP) will be sent to your email address"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Address Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Address Information</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Street Address"
                  value={formData.location.address}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    location: { ...prev.location, address: e.target.value }
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={formData.location.city}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    location: { ...prev.location, city: e.target.value }
                  }))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={formData.location.postcode}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    location: { ...prev.location, postcode: e.target.value }
                  }))}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Country"
                  value={formData.location.country}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    location: { ...prev.location, country: e.target.value }
                  }))}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Account Security Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 2 }}>Account Security</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  required
                />
              </Grid>
            </Grid>
          </Paper>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            disabled={isSubmitting}
            onClick={handleCreateIndividual}
          >
            {isSubmitting ? 'Creating Account...' : 'Submit'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );

  const renderStep3 = () => (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 3 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>Confirm Your Order</Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack spacing={2}>
          <Typography variant="subtitle1">Selected Number: {(phoneNumbers.find((number: any) => number.id === selectedNumber) as any)?.value}</Typography>
          <Typography variant="subtitle1">SIM Type: {simType === 'physical' ? 'Physical SIM' : 'eSIM'}</Typography>
          <Typography variant="subtitle1">Name: {formData.title} {formData.fullName}</Typography>
          <Typography variant="subtitle1">Preferred Name: {formData.preferredGivenName}</Typography>
          <Typography variant="subtitle1">Contact: {formData.phoneNumber}</Typography>
          <Typography variant="subtitle1">Email: {formData.email}</Typography>
          <Typography variant="subtitle1">Address: {`${formData.location.address}, ${formData.location.city}, ${formData.location.postcode}, ${formData.location.country}`}</Typography>
        </Stack>
      </Paper>
      
      <Button
        variant="contained"
        color="primary"
        fullWidth
        disabled={isSubmitting}
        onClick={handleSubmitOrder}
      >
        {isSubmitting ? 'Processing...' : 'Confirm Order'}
      </Button>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <StepIcon active={currentStep >= 1} theme={theme}>1</StepIcon>
          <StepIcon active={currentStep >= 2} theme={theme}>2</StepIcon>
          <StepIcon active={currentStep >= 3} theme={theme}>3</StepIcon>
        </Stack>
        <Typography variant="h6" color="primary.main" sx={{ mb: 1 }}>
          {currentStep === 1 && "Hello, get a mobile line in a jiffy."}
          {currentStep === 2 && "Create your account"}
          {currentStep === 3 && "Confirm your order"}
        </Typography>
      </Box>

      {currentStep === 1 && renderStep1()}
      {currentStep === 2 && renderStep2()}
      {currentStep === 3 && renderStep3()}

      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setToast(prev => ({ ...prev, open: false }))} 
          severity={toast.severity as AlertColor}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default BuyProduct;
